import React from "react";
import type { TFunction } from "i18next";
import {
  CircleArrowDown,
  CircleArrowUp,
  CircleMinus,
  CircleOutline,
  CirclePlus
} from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text, { type TextVariant } from "@/components/ui/text";
import {
  displayTotalCurrency,
  formatNumber,
  getTermsCurrencySign
} from "@/utils/numbers";
import { useAccountCurrency } from "@/services/hooks/useCurrencyConversion";
import type { CompareImpact, MetricDelta } from "./types";
import { deltaSign, type CompareTranslationKey } from "./columnIds";

interface ImpactAppearance {
  icon: React.ReactNode;
  /** Undefined leaves the colour to the text variant — used for "no change". */
  color?: string;
  textVariant: TextVariant;
}

/**
 * Typed as a total record so adding an impact label without giving it an icon,
 * a colour and a variant is a compile error rather than a blank cell.
 */
const IMPACT_APPEARANCE: Record<CompareImpact, ImpactAppearance> = {
  new: {
    icon: <CirclePlus />,
    color: "var(--charts-positive)",
    textVariant: "body"
  },
  upsell: {
    icon: <CircleArrowUp />,
    color: "var(--charts-positive)",
    textVariant: "body"
  },
  downsell: {
    icon: <CircleArrowDown />,
    color: "var(--charts-negative)",
    textVariant: "body"
  },
  churn: {
    icon: <CircleMinus />,
    color: "var(--charts-negative)",
    textVariant: "body"
  },
  noChange: { icon: <CircleOutline />, textVariant: "bodyTertiary" }
};

interface ImpactCellProps {
  impact: CompareImpact;
  t: TFunction;
  /** The grain's Compare-to namespace; the labels live under `.impactValues`. */
  translationKey: CompareTranslationKey;
}

/** Icon plus label in the impact's colour — no chip, matching the design. */
export const ImpactCell: React.FC<ImpactCellProps> = ({
  impact,
  t,
  translationKey
}) => {
  // The map is total over CompareImpact, but the value arrives over the wire —
  // a label the backend adds later would destructure undefined and take the
  // cell's whole render down. The neutral appearance is the safe stand-in; the
  // label still comes from `impact`, so an unmapped value is visible rather
  // than silently relabelled.
  const { icon, color, textVariant } =
    IMPACT_APPEARANCE[impact] ?? IMPACT_APPEARANCE.noChange;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <IconContainer
        size="5"
        color={color ? undefined : "tertiary"}
        style={color ? { color } : undefined}
      >
        {icon}
      </IconContainer>
      <Text
        variant={textVariant}
        // min-w-0 goes on the flex item that truncates; without it the item's
        // auto min-width keeps it from ever shrinking and `truncate` is inert.
        className="truncate min-w-0"
        style={color ? { color } : undefined}
      >
        {t(`${translationKey}.impactValues.${impact}`)}
      </Text>
    </div>
  );
};

interface DeltaValueCellProps {
  metric: MetricDelta | undefined;
  currency: string | null;
  /** Terms-currency columns render in the row's own currency, not the account's. */
  isTermsCurrency: boolean;
}

/** The signed movement, formatted in whichever currency the column is on. */
export const DeltaValueCell: React.FC<DeltaValueCellProps> = ({
  metric,
  currency,
  isTermsCurrency
}) => {
  // Read unconditionally — a terms delta column sits beside its
  // account-currency twin, so a currency sharing that one's glyph needs a sign
  // that tells the two apart, exactly as the paired value columns do.
  const accountCurrency = useAccountCurrency();
  const delta = metric?.delta;
  // Null is "one side had no figure", which is not the same as no movement.
  // Rendering it as 0 would report a contract with no prior record as flat.
  if (delta == null) return null;

  // A terms figure with no currency recorded against it is shown unadorned:
  // falling through to the account currency would label it as something it is
  // demonstrably not.
  const magnitude =
    isTermsCurrency && !currency
      ? formatNumber(Math.abs(delta), 0, false, false).value
      : displayTotalCurrency(
          Math.abs(delta),
          false,
          undefined,
          isTermsCurrency ? currency ?? undefined : undefined,
          isTermsCurrency && currency
            ? getTermsCurrencySign(currency, accountCurrency)
            : undefined
        );
  // Withheld when the magnitude rounds away at this column's precision — see
  // `deltaSign`, which the totals-bar tile shares so the two cannot disagree.
  // These are whole-currency columns, which is the `0` below: both branches
  // above format with no fraction digits. The Impact column still carries the
  // direction, so nothing is lost.
  const sign = deltaSign(delta, 0);

  return <Text variant="body">{`${sign}${magnitude}`}</Text>;
};
